import React from 'react';

import { socialCardKeys } from '@/src/constants/socialcard.constant';
import useLazyHlsPlayback from '@/src/components/hooks/useLazyHlsPlayback';
import { MediaBox, MediaOverlay, WaitingSpinner, MediaEnclosure, MediaSummary, playerRadius } from './StudioMediaParts';

/**
 * The studio video player.
 *
 * Its own module because it is the only thing in the set that pulls hls.js
 * (~169 kB in the bundle). StudioBody loads it with next/dynamic, so an embed
 * on an older widget type - or on a studio type showing only text - never
 * downloads the HLS runtime. Import this statically and that cost lands on
 * every embed page instead.
 */
/** Any thumbnail we can show for a video, in order of preference. */
const videoPoster = (cardItem) => {
  if (!cardItem) return null;
  if (cardItem[socialCardKeys.customThumbnailUrl]) return cardItem[socialCardKeys.customThumbnailUrl];
  if (cardItem[socialCardKeys.messageVideoThumbnail]) return cardItem[socialCardKeys.messageVideoThumbnail];
  if (cardItem[socialCardKeys.videoPlaybackId])
    return `https://image.mux.com/${cardItem[socialCardKeys.videoPlaybackId]}/thumbnail.jpg?time=1`;
  return null;
};

export const StudioVideo = ({ t, cardItem, enclosure, summaryStyle, objectFit = 'cover' }) => {
  const playbackId = cardItem && cardItem[socialCardKeys.videoPlaybackId];
  const src = `https://stream.mux.com/${playbackId}.m3u8?default_subtitles_lang=en`;

  /* Hooks run unconditionally; the hook is lazy, so with no playback id it
     simply never initialises (the fallback branch below never plays). */
  const {
    videoRef,
    isPlaying,
    isWaiting,
    togglePlay,
    handlePlay,
    handlePlaying,
    handlePause,
    handleWaiting,
    handleEnded,
  } = useLazyHlsPlayback(src);

  /* Reserved until the poster reports its real shape. 4/3 rather than portrait
     so a masonry column doesn't jump from a tall placeholder to a short video. */
  const [ratio, setRatio] = React.useState(4 / 3);

  const poster = videoPoster(cardItem);
  const processing = cardItem && cardItem[socialCardKeys.isVideoProcessing];
  const radius = playerRadius(enclosure, cardItem);

  /* Reserve the real aspect ratio from the poster's natural size, then
     relayout - the onLoad -> refresh() pattern the other video cards use. */
  const posterSizer = poster ? (
    <img
      src={poster}
      alt=""
      aria-hidden="true"
      style={{ display: 'none' }}
      onLoad={(e) => {
        const w = e.target.naturalWidth;
        const h = e.target.naturalHeight;
        if (w && h) setRatio(w / h);
        if (t.refresh) t.refresh();
      }}
    />
  ) : null;

  /* No Mux id, or a broken one: paint the player exactly as the older widget
     types do - black frame, play control, name and work title - it simply
     won't play. Never swap in a placeholder label: a card reading "Video
     testimonial" looks broken in a way an inert player does not. */
  if (!playbackId) {
    return (
      <MediaEnclosure enclosure={enclosure} cardItem={cardItem}>
        {posterSizer}
        <MediaBox ratio={ratio} radius={radius}>
          {processing ? (
            <span
              className="text-yellow-800 bg-yellow-100 px-2 py-0.5 text-xs font-medium rounded-full"
              style={{ position: 'absolute', top: 8, left: 8, zIndex: 20 }}
            >
              Processing Video
            </span>
          ) : null}
          {poster ? (
            <img src={poster} alt="" aria-hidden="true" style={{ width: '100%', height: '100%', objectFit }} />
          ) : null}
          <MediaOverlay cardItem={cardItem} onPlay={() => {}} fontClass={t.fontClass} />
        </MediaBox>
        <MediaSummary t={t} cardItem={cardItem} color={enclosure && enclosure.color} summaryStyle={summaryStyle} />
      </MediaEnclosure>
    );
  }

  return (
    <MediaEnclosure enclosure={enclosure} cardItem={cardItem}>
      {posterSizer}
      <MediaBox ratio={ratio} radius={radius}>
        {processing ? (
          <span
            className="text-yellow-800 bg-yellow-100 px-2 py-0.5 text-xs font-medium rounded-full"
            style={{ position: 'absolute', top: 8, left: 8, zIndex: 20 }}
          >
            Processing Video
          </span>
        ) : null}

        <video
          ref={videoRef}
          className="w-full h-full cursor-pointer"
          style={{
            width: '100%',
            height: '100%',
            borderRadius: radius,
            objectFit,
            backgroundColor: '#000',
            display: 'block',
          }}
          poster={poster || undefined}
          playsInline
          autoPlay={false}
          preload="none"
          controlsList="noremoteplayback"
          onClick={() => togglePlay()}
          onPlay={handlePlay}
          onPlaying={handlePlaying}
          onPause={handlePause}
          onWaiting={handleWaiting}
          onEnded={handleEnded}
          onLoadedMetadata={() => t.refresh && t.refresh()}
        />

        {!isPlaying ? (
          <MediaOverlay cardItem={cardItem} onPlay={() => togglePlay()} fontClass={t.fontClass} />
        ) : null}
        {isWaiting ? <WaitingSpinner /> : null}
      </MediaBox>
      <MediaSummary t={t} cardItem={cardItem} color={enclosure && enclosure.color} summaryStyle={summaryStyle} />
    </MediaEnclosure>
  );
};

export default StudioVideo;
